import { z } from "zod";

// =============================================================================
// Vendor Schemas
// =============================================================================

export const VendorCreateSchema = z.object({
  name: z.string().min(1).max(255),
  slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/),
  logo: z.string().optional(),
  website: z.string().url().optional(),
  active: z.boolean().optional().default(true),
  sortOrder: z.number().int().optional().default(0),
});

export const VendorUpdateSchema = VendorCreateSchema.partial();

export const VendorResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  logo: z.string().nullable(),
  website: z.string().nullable(),
  active: z.boolean(),
  sortOrder: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// =============================================================================
// Category Schemas
// =============================================================================

export const CategoryCreateSchema = z.object({
  name: z.string().min(1).max(255),
  slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/),
  icon: z.string().optional(),
  active: z.boolean().optional().default(true),
  sortOrder: z.number().int().optional().default(0),
});

export const CategoryUpdateSchema = CategoryCreateSchema.partial();

export const CategoryResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
  active: z.boolean(),
  sortOrder: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// =============================================================================
// Product Schemas
// =============================================================================

export const ProductDimensionsSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
  depth: z.number().positive(),
  unit: z.enum(["cm", "mm", "m", "in"]).default("cm"),
});

export const ProductCreateSchema = z.object({
  sku: z.string().min(1).max(50),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  dimensions: ProductDimensionsSchema.optional(),
  glbKey: z.string().optional(),
  textureKey: z.string().optional(),
  thumbnailKey: z.string().optional(),
  price: z.number().positive().optional(),
  currency: z.string().length(3).optional().default("ILS"),
  active: z.boolean().optional().default(true),
  featured: z.boolean().optional().default(false),
  vendorId: z.string().min(1),
  categoryId: z.string().min(1),
});

export const ProductUpdateSchema = ProductCreateSchema.partial().omit({
  sku: true,
});

export const ProductResponseSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  dimensions: ProductDimensionsSchema.nullable(),
  glbKey: z.string().nullable(),
  textureKey: z.string().nullable(),
  thumbnailKey: z.string().nullable(),
  price: z.number().nullable(),
  currency: z.string(),
  active: z.boolean(),
  featured: z.boolean(),
  vendorId: z.string(),
  categoryId: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Expanded relations (optional, included when fetching list with includes)
  vendor: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
  }).optional(),
  category: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
  }).optional(),
});

export const ProductListQuerySchema = z.object({
  vendorId: z.string().optional(),
  categoryId: z.string().optional(),
  active: z.enum(["true", "false"]).optional(),
  featured: z.enum(["true", "false"]).optional(),
  search: z.string().optional(),
  moduleType: z.enum(["base", "wall", "tall", "corner", "island"]).optional(),
  limit: z.string().regex(/^\d+$/).optional().default("50"),
  offset: z.string().regex(/^\d+$/).optional().default("0"),
});

// =============================================================================
// Product Variant Schemas (BYK-75: Catalog Schema)
// =============================================================================

/**
 * Material types for products
 */
export const ProductMaterialSchema = z.enum([
  // Wood finishes
  "oak",
  "walnut",
  "maple",
  "birch",
  "cherry",
  // Laminate finishes
  "white-laminate",
  "gray-laminate",
  "black-laminate",
  // Metal finishes
  "stainless-steel",
  "brushed-nickel",
  "chrome",
  "matte-black",
  "brass",
  "copper",
  // Stone surfaces
  "granite",
  "quartz",
  "marble",
  "neolith",
  // Solid surfaces
  "corian",
  "concrete",
  "butcher-block",
  // Painted
  "painted-white",
  "painted-gray",
  "painted-custom",
]);

/**
 * Surface finish types
 */
export const SurfaceFinishSchema = z.enum([
  "matte",
  "satin",
  "semi-gloss",
  "gloss",
  "textured",
  "brushed",
  "polished",
  "honed",
  "leathered",
]);

/**
 * Color configuration for variants
 */
export const ProductColorSchema = z.object({
  /** Display name for the color */
  name: z.string().min(1).max(50),
  /** Hex color code for preview */
  hex: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** Optional secondary color for patterns */
  secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});

/**
 * Product variant representing a specific configuration
 * (e.g., "Chrome" finish for a sink, "Matte Black" for hardware)
 */
export const ProductVariantCreateSchema = z.object({
  /** Unique SKU suffix for this variant (e.g., "CHR" for chrome) */
  skuSuffix: z.string().min(1).max(20).regex(/^[A-Z0-9-]+$/),
  /** Display name (e.g., "Chrome", "Brushed Nickel") */
  name: z.string().min(1).max(100),
  /** Material type */
  material: ProductMaterialSchema.optional(),
  /** Surface finish */
  finish: SurfaceFinishSchema.optional(),
  /** Color configuration */
  color: ProductColorSchema.optional(),
  /** Price modifier (can be positive or negative) */
  priceModifier: z.number().optional().default(0),
  /** R2 key for variant-specific 3D model (if different from base) */
  glbKey: z.string().optional(),
  /** R2 key for variant-specific texture */
  textureKey: z.string().optional(),
  /** R2 key for variant thumbnail */
  thumbnailKey: z.string().optional(),
  /** PBR material properties */
  pbrProperties: z.object({
    /** Base color (hex) */
    baseColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    /** Metallic factor 0-1 */
    metallic: z.number().min(0).max(1).optional(),
    /** Roughness factor 0-1 */
    roughness: z.number().min(0).max(1).optional(),
    /** Normal map R2 key */
    normalMapKey: z.string().optional(),
    /** Ambient occlusion R2 key */
    aoMapKey: z.string().optional(),
  }).optional(),
  /** Whether this variant is active */
  active: z.boolean().optional().default(true),
  /** Whether this is the default variant */
  isDefault: z.boolean().optional().default(false),
  /** Sort order for display */
  sortOrder: z.number().int().optional().default(0),
});

export const ProductVariantUpdateSchema = ProductVariantCreateSchema.partial();

export const ProductVariantResponseSchema = z.object({
  id: z.string(),
  productId: z.string(),
  skuSuffix: z.string(),
  name: z.string(),
  material: ProductMaterialSchema.nullable(),
  finish: SurfaceFinishSchema.nullable(),
  color: ProductColorSchema.nullable(),
  priceModifier: z.number(),
  glbKey: z.string().nullable(),
  textureKey: z.string().nullable(),
  thumbnailKey: z.string().nullable(),
  pbrProperties: z.object({
    baseColor: z.string().nullable(),
    metallic: z.number().nullable(),
    roughness: z.number().nullable(),
    normalMapKey: z.string().nullable(),
    aoMapKey: z.string().nullable(),
  }).nullable(),
  active: z.boolean(),
  isDefault: z.boolean(),
  sortOrder: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/**
 * Product with all its variants (for catalog display)
 */
export const ProductWithVariantsResponseSchema = ProductResponseSchema.extend({
  vendor: VendorResponseSchema.optional(),
  category: CategoryResponseSchema.optional(),
  variants: z.array(ProductVariantResponseSchema),
  /** Full SKU with default variant suffix */
  fullSku: z.string(),
  /** Final price including default variant modifier */
  finalPrice: z.number().nullable(),
});

/**
 * Semantic metadata for product 3D placement
 */
export const ProductSemanticsSchema = z.object({
  /** True for sinks, dishwashers */
  isWaterSource: z.boolean().default(false),
  /** True for ovens, cooktops */
  isHeatSource: z.boolean().default(false),
  /** True for fridges, freezers */
  isColdStorage: z.boolean().default(false),
  /** Requires front access for doors */
  requiresFrontAccess: z.boolean().default(false),
  /** Front access depth in cm */
  frontAccessDepth: z.number().default(0),
  /** Requires ventilation (cooktops) */
  requiresVentilation: z.boolean().default(false),
  /** Can be mounted on counter */
  isCounterMountable: z.boolean().default(false),
  /** Is floor-standing (fridges, tall cabinets) */
  isFloorStanding: z.boolean().default(false),
});

/**
 * Extended product metadata for 3D visualization
 */
export const Product3DMetadataSchema = z.object({
  /** GLB model complexity level */
  lodLevels: z.array(z.object({
    level: z.enum(["high", "medium", "low"]),
    glbKey: z.string(),
    maxDistance: z.number(),
  })).optional(),
  /** Bounding box dimensions (auto-calculated from model) */
  boundingBox: z.object({
    width: z.number(),
    height: z.number(),
    depth: z.number(),
  }).optional(),
  /** Origin offset from model center */
  originOffset: z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
  }).optional(),
  /** Rotation offset */
  rotationOffset: z.number().optional(),
  /** Semantic metadata for AI placement */
  semantics: ProductSemanticsSchema.optional(),
});

// =============================================================================
// BYK-77: Cabinet Module Schemas
// =============================================================================

export const ModuleTypeSchema = z.enum([
  "base",
  "wall",
  "tall",
  "corner",
  "island",
  "filler",
]);

export const CabinetInternalConfigSchema = z.object({
  doors: z.enum(["single", "double", "none"]).optional(),
  drawers: z.number().int().min(0).max(6).optional(),
  shelves: z.number().int().min(0).max(5).optional(),
});

export const HandleConfigSchema = z.object({
  position: z.enum(["top", "center", "bottom"]),
  orientation: z.enum(["horizontal", "vertical"]),
});

export const SnapPointSchema = z.object({
  side: z.enum(["left", "right", "top", "bottom", "front", "back"]),
  offset: z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
  }),
});

export const CabinetModuleCreateSchema = z.object({
  name: z.string().min(1).max(255),
  sku: z.string().min(1).max(50),
  moduleType: ModuleTypeSchema,
  supportedWidths: z.array(z.number().int().positive()),
  defaultWidth: z.number().int().positive(),
  defaultHeight: z.number().int().positive(),
  defaultDepth: z.number().int().positive(),
  internalConfig: CabinetInternalConfigSchema.optional(),
  handleConfig: HandleConfigSchema.optional(),
  glbKey: z.string().optional(),
  glbKeyLow: z.string().optional(),
  compatibleHandles: z.array(z.string()).optional(),
  snapPoints: z.array(SnapPointSchema).optional(),
  active: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  vendorId: z.string().optional(),
});

export const CabinetModuleUpdateSchema = CabinetModuleCreateSchema.partial().omit({
  sku: true,
});

export const CabinetModuleResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  sku: z.string(),
  moduleType: ModuleTypeSchema,
  supportedWidths: z.array(z.number()),
  defaultWidth: z.number(),
  defaultHeight: z.number(),
  defaultDepth: z.number(),
  internalConfig: CabinetInternalConfigSchema.nullable(),
  handleConfig: HandleConfigSchema.nullable(),
  glbKey: z.string().nullable(),
  glbKeyLow: z.string().nullable(),
  compatibleHandles: z.array(z.string()).nullable(),
  snapPoints: z.array(SnapPointSchema).nullable(),
  active: z.boolean(),
  sortOrder: z.number(),
  vendorId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// =============================================================================
// Handle Product Schemas
// =============================================================================

export const HandleTypeSchema = z.enum([
  "bar",
  "knob",
  "cup",
  "edge",
  "integrated",
]);

export const HandleFinishOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  metallic: z.number().min(0).max(1),
  roughness: z.number().min(0).max(1),
});

export const HandleProductCreateSchema = z.object({
  name: z.string().min(1).max(255),
  sku: z.string().min(1).max(50),
  handleType: HandleTypeSchema,
  length: z.number().int().positive(),
  width: z.number().int().positive(),
  projection: z.number().int().positive(),
  holeCenters: z.number().int().positive().optional(),
  glbKey: z.string().optional(),
  finishOptions: z.array(HandleFinishOptionSchema).optional(),
  price: z.number().positive().optional(),
  currency: z.string().length(3).default("ILS"),
  active: z.boolean().default(true),
  vendorId: z.string().optional(),
});

export const HandleProductUpdateSchema = HandleProductCreateSchema.partial().omit({
  sku: true,
});

export const HandleProductResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  sku: z.string(),
  handleType: HandleTypeSchema,
  length: z.number(),
  width: z.number(),
  projection: z.number(),
  holeCenters: z.number().nullable(),
  glbKey: z.string().nullable(),
  finishOptions: z.array(HandleFinishOptionSchema).nullable(),
  price: z.number().nullable(),
  currency: z.string(),
  active: z.boolean(),
  vendorId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// =============================================================================
// File Upload Schemas
// =============================================================================

export const PresignRequestSchema = z.object({
  filename: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(100),
  entityType: z.enum(["vendor", "product", "project", "render", "general"]),
  entityId: z.string().optional(),
  fileSize: z.number().positive().optional(),
});

export const PresignResponseSchema = z.object({
  token: z.string(),
  key: z.string(),
  expiresIn: z.number(),
  maxSize: z.number(),
});

export const UploadCompleteSchema = z.object({
  token: z.string(),
});

export const FileResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  size: z.number(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  createdAt: z.string().datetime(),
});

// =============================================================================
// API Response Schemas
// =============================================================================

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  });

export const ErrorResponseSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

// =============================================================================
// Auth Schemas (for BetterAuth compatibility)
// =============================================================================

export const SessionResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    image: z.string().nullable(),
    role: z.string(),
  }),
  session: z.object({
    id: z.string(),
    expiresAt: z.coerce.date(),
  }),
});

// =============================================================================
// BYK-6: AI Render Schemas
// =============================================================================

export const RenderStyleSchema = z.enum([
  "photorealistic",
  "sketch",
  "blueprint",
  "minimal",
]);

export const RenderCameraSchema = z.enum([
  "front",
  "corner",
  "top-down",
  "eye-level",
]);

export const RenderLightingSchema = z.enum([
  "daylight",
  "evening",
  "studio",
  "dramatic",
]);

export const RenderStatusSchema = z.enum([
  "pending",
  "processing",
  "completed",
  "failed",
]);

/**
 * Input schema for creating a new render
 */
export const RenderCreateSchema = z.object({
  projectId: z.string().min(1),
  style: RenderStyleSchema.default("photorealistic"),
  prompt: z.string().optional(),
  cameraAngle: RenderCameraSchema.default("corner"),
  lightingMood: RenderLightingSchema.default("daylight"),
  sync: z.boolean().optional().default(false),
  enableKashrut: z.boolean().optional().default(false),
});

/**
 * Render metadata stored in JSON field
 */
export const RenderMetadataSchema = z.object({
  cameraAngle: RenderCameraSchema.optional(),
  lightingMood: RenderLightingSchema.optional(),
  enableKashrut: z.boolean().optional(),
  requestedAt: z.string().datetime().optional(),
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  failedAt: z.string().datetime().optional(),
  mimeType: z.string().optional(),
  error: z.string().optional(),
});

/**
 * Response schema for a single render
 */
export const RenderResponseSchema = z.object({
  id: z.string(),
  r2Key: z.string(),
  prompt: z.string().nullable(),
  style: RenderStyleSchema.nullable(),
  status: RenderStatusSchema,
  error: z.string().nullable(),
  metadata: RenderMetadataSchema.nullable(),
  projectId: z.string(),
  project: z.object({
    id: z.string(),
    name: z.string(),
  }).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Optional: included in sync response
  imageUrl: z.string().optional(),
});

/**
 * Paginated render list response
 */
export const RenderListResponseSchema = PaginatedResponseSchema(RenderResponseSchema);

/**
 * Real-time render progress (sent via WebSocket)
 */
export const RenderProgressSchema = z.object({
  renderId: z.string(),
  status: RenderStatusSchema,
  progress: z.number().min(0).max(100),
  stage: z.string(),
  imageUrl: z.string().optional(),
  error: z.string().optional(),
  updatedAt: z.string().datetime(),
});

/**
 * WebSocket message for render progress
 */
export const RenderProgressMessageSchema = z.object({
  type: z.literal("status"),
  data: RenderProgressSchema,
});

// =============================================================================
// BYK-141: Multi-Turn Refinement with Thought Signatures
// =============================================================================

/**
 * Maximum refinement iterations allowed
 * Gemini thought signatures maintain context across iterations
 */
export const MAX_REFINEMENT_DEPTH = 5;

/**
 * Input schema for refining an existing render
 */
export const RenderRefineCreateSchema = z.object({
  /** User's refinement instruction */
  refinementPrompt: z.string().min(1).max(2000),
  /** Optional: Override render style */
  style: RenderStyleSchema.optional(),
  /** Optional: Override camera angle */
  cameraAngle: RenderCameraSchema.optional(),
  /** Optional: Override lighting mood */
  lightingMood: RenderLightingSchema.optional(),
});

/**
 * Response schema for a render with refinement chain info
 */
export const RenderWithRefinementsResponseSchema = RenderResponseSchema.extend({
  /** Refinement depth (0 = original, 1-5 = refinement iterations) */
  refinementDepth: z.number().int().min(0).max(MAX_REFINEMENT_DEPTH),
  /** User's refinement prompt (if this is a refinement) */
  refinementPrompt: z.string().nullable(),
  /** Parent render ID (if this is a refinement) */
  parentRenderId: z.string().nullable(),
  /** Child renders (subsequent refinements) */
  childRenders: z.array(z.object({
    id: z.string(),
    refinementPrompt: z.string().nullable(),
    status: RenderStatusSchema,
    createdAt: z.string().datetime(),
  })).optional(),
  /** Whether this render can be refined (has thought signature and depth < max) */
  canRefine: z.boolean(),
});

/**
 * Render lineage response (full chain from root to current)
 */
export const RenderLineageResponseSchema = z.object({
  /** Current render */
  current: RenderWithRefinementsResponseSchema,
  /** Full ancestry chain (root → parent → current) */
  ancestors: z.array(z.object({
    id: z.string(),
    refinementPrompt: z.string().nullable(),
    refinementDepth: z.number(),
    status: RenderStatusSchema,
    createdAt: z.string().datetime(),
    imageUrl: z.string().optional(),
  })),
  /** Root render (original, depth=0) */
  rootRenderId: z.string(),
});
